// Performance optimization utilities

// Lazy loading utility for images
export const lazyLoadImage = (img) => {
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const image = entry.target;
        image.src = image.dataset.src;
        image.classList.remove('lazy');
        imageObserver.unobserve(image);
      }
    });
  });
  
  if (img) {
    imageObserver.observe(img);
  }
};

// Preload critical resources
export const preloadCriticalResources = () => {
  // Preload critical CSS
  const criticalCSS = [
    '/static/css/main.css',
    'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap'
  ];
  
  criticalCSS.forEach(href => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = href;
    link.onload = function() {
      this.onload = null;
      this.rel = 'stylesheet';
    };
    document.head.appendChild(link);
  });
};

// Debounce utility for performance
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle utility for scroll events
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Web Vitals optimization and monitoring
export const optimizeWebVitals = () => {
  // Largest Contentful Paint (LCP) optimization
  const optimizeLCP = () => {
    // Preload critical resources
    const criticalResources = [
      '/static/css/main.css',
      '/static/js/bundle.js'
    ];
    
    criticalResources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource;
      link.as = resource.endsWith('.css') ? 'style' : 'script';
      document.head.appendChild(link);
    });
    
    // Optimize hero images
    const heroImages = document.querySelectorAll('img[fetchpriority="high"]');
    heroImages.forEach(img => {
      if (!img.complete) {
        img.loading = 'eager';
        img.decoding = 'sync';
      }
    });
  };
  
  // First Input Delay (FID) optimization
  const optimizeFID = () => {
    // Break up long tasks using scheduler API or setTimeout
    const scheduleTask = (callback, priority = 'background') => {
      if ('scheduler' in window && 'postTask' in window.scheduler) {
        window.scheduler.postTask(callback, { priority });
      } else {
        setTimeout(callback, 0);
      }
    };
    
    // Defer non-critical JavaScript
    scheduleTask(() => {
      // Initialize analytics, social widgets, etc.
      initializeNonCriticalFeatures();
    });
  };
  
  // Cumulative Layout Shift (CLS) optimization
  const optimizeCLS = () => {
    // Add size attributes to images
    const images = document.querySelectorAll('img:not([width]):not([height])');
    images.forEach(img => {
      img.addEventListener('load', () => {
        if (!img.width || !img.height) {
          img.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
        }
      });
    });
    
    // Reserve space for dynamic content
    const dynamicContainers = document.querySelectorAll('[data-dynamic-content]');
    dynamicContainers.forEach(container => {
      if (!container.style.minHeight) {
        container.style.minHeight = '200px'; // Reserve minimum space
      }
    });
  };
  
  optimizeLCP();
  optimizeFID();
  optimizeCLS();
};

// Initialize non-critical features
const initializeNonCriticalFeatures = () => {
  // Analytics
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('config', 'GA_MEASUREMENT_ID');
  }
  
  // Social media widgets
  loadSocialWidgets();
  
  // Third-party scripts
  loadThirdPartyScripts();
};

// Load social media widgets
const loadSocialWidgets = () => {
  // Lazy load social media buttons
  const socialContainers = document.querySelectorAll('[data-social-widget]');
  if (socialContainers.length > 0) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          loadSocialWidget(entry.target);
          observer.unobserve(entry.target);
        }
      });
    });
    
    socialContainers.forEach(container => observer.observe(container));
  }
};

// Load social widget
const loadSocialWidget = (container) => {
  const widgetType = container.dataset.socialWidget;
  // Implementation for different social widgets
  console.log(`Loading ${widgetType} widget`);
};

// Load third-party scripts
const loadThirdPartyScripts = () => {
  // Load scripts that are not critical for initial render
  const scripts = [
    // Add third-party script URLs here
  ];
  
  scripts.forEach(src => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });
};

// Performance monitoring
export const monitorPerformance = () => {
  // Monitor Core Web Vitals
  if ('web-vitals' in window) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(sendToAnalytics);
      getFID(sendToAnalytics);
      getFCP(sendToAnalytics);
      getLCP(sendToAnalytics);
      getTTFB(sendToAnalytics);
    });
  }
  
  // Monitor custom metrics
  monitorCustomMetrics();
};

// Send metrics to analytics
const sendToAnalytics = (metric) => {
  // Send to Google Analytics or other analytics service
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`${metric.name}: ${metric.value}`);
  }
};

// Monitor custom performance metrics
const monitorCustomMetrics = () => {
  // Time to Interactive (TTI)
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'navigation') {
          const tti = entry.loadEventEnd - entry.navigationStart;
          sendToAnalytics({
            name: 'TTI',
            value: tti,
            id: 'custom-tti'
          });
        }
      }
    });
    
    observer.observe({ entryTypes: ['navigation'] });
  }
  
  // Resource loading performance
  monitorResourceLoading();
};

// Monitor resource loading
const monitorResourceLoading = () => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 1000) { // Resources taking more than 1 second
          console.warn(`Slow resource: ${entry.name} took ${entry.duration}ms`);
        }
      }
    });
    
    observer.observe({ entryTypes: ['resource'] });
  }
};

// Resource hints for better performance
export const addResourceHints = () => {
  const hints = [
    { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
    { rel: 'dns-prefetch', href: '//fonts.gstatic.com' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true }
  ];
  
  hints.forEach(hint => {
    const link = document.createElement('link');
    Object.keys(hint).forEach(key => {
      if (key === 'crossorigin') {
        link.crossOrigin = hint[key];
      } else {
        link[key] = hint[key];
      }
    });
    document.head.appendChild(link);
  });
};

// Initialize all performance optimizations
export const initPerformanceOptimizations = () => {
  // Run optimizations when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      optimizeWebVitals();
      addResourceHints();
      monitorPerformance();
    });
  } else {
    optimizeWebVitals();
    addResourceHints();
    monitorPerformance();
  }
};